import React, { useState, useEffect } from "react";
import {
  Stack,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  Typography,
  InputLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

/**
 * Barra de búsqueda + dropdowns configurables para tablas del Care Manager.
 * Sigue el patrón visual de RecallFilters.jsx.
 *
 * @param {{
 *   searchValue: string,
 *   onSearchChange: (value: string) => void,
 *   searchPlaceholder?: string,
 *   filters?: Array<{ key: string, label: string, options: string[], value: string }>,
 *   onFilterChange?: (key: string, value: string) => void
 * }} props
 */
export default function TableSearchBar({
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search patient...",
  filters = [],
  onFilterChange,
}) {
  const [searchInput, setSearchInput] = useState(searchValue || "");

  // Sync external value changes
  useEffect(() => {
    setSearchInput(searchValue || "");
  }, [searchValue]);

  // Debounce search 300ms
  useEffect(() => {
    const timer = setTimeout(() => {
      onSearchChange(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      alignItems={{ xs: "stretch", sm: "center" }}
      sx={{ mb: 1.5, flexWrap: "wrap" }}
    >
      <TextField
        size="small"
        placeholder={searchPlaceholder}
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
              </InputAdornment>
            ),
          },
        }}
        sx={{
          minWidth: 200,
          "& .MuiOutlinedInput-root": { borderRadius: 2 },
        }}
      />

      {filters.map((f) => (
        <FormControl key={f.key} size="small" sx={{ minWidth: 130 }}>
          <InputLabel sx={{ fontSize: "0.8rem" }}>{f.label}</InputLabel>
          <Select
            value={f.value}
            label={f.label}
            onChange={(e) => onFilterChange?.(f.key, e.target.value)}
            sx={{ borderRadius: 2, fontSize: "0.8rem" }}
          >
            {f.options.map((opt) => (
              <MenuItem key={opt} value={opt}>
                <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                  {opt}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ))}
    </Stack>
  );
}
